"use client"

import type React from "react"
import { useState } from "react"
import { Expand } from "lucide-react"
import Image from "next/image"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { VideoPlayer } from "./video-player"
import { ShowcaseProject } from "@/types/showcase"

interface ShowcaseCardProps {
  project: ShowcaseProject
}

export function ShowcaseCard({ project }: ShowcaseCardProps) {
  const [expanded, setExpanded] = useState(false)

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpanded(!expanded)
  }

  // Check if the URL is a video
  const isVideo = project.is_video || 
    project.image_url?.toLowerCase().endsWith(".mp4") ||
    project.image_url?.toLowerCase().endsWith(".mov") ||
    project.image_url?.toLowerCase().endsWith(".webm")

  // Glassmorphism style
  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.21)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '20px',
    border: '0.25px solid rgba(255, 255, 255, 0.1)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 42px 21px rgba(255, 255, 255, 0.05)
    `
  }

  return (
    <>
      <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden !bg-transparent relative" style={glassmorphismStyle}>
        {/* Top highlight line */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
          }}
        />
        {/* Left highlight line */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1px',
            height: '100%',
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
          }}
        />
        <CardContent className="p-0">
          {/* Project Video/Image */}
          <div className="relative aspect-square overflow-hidden bg-zinc-900">
            {isVideo ? (
              <VideoPlayer
                src={project.image_url}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <Image
                src={
                  project.image_url ||
                  `/placeholder.svg?height=400&width=400&query=prototype%20of%20${
                    encodeURIComponent(project.product) || "product"
                  }%20for%20${encodeURIComponent(project.customer)}`
                }
                alt={project.title}
                width={400}
                height={400}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            )}

            {/* Expand button */}
            <button
              onClick={toggleExpand}
              className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
              aria-label="View full project"
            >
              <Expand className="w-4 h-4" />
            </button>

            {/* Project type badge */}
            <div className="absolute top-3 left-3">
              <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
                {project.product}
              </Badge>
            </div>
          </div>

          {/* Project Info */}
          <div className="p-6 space-y-4">
            <div>
              <h3 className="text-xl font-semibold mb-2 line-clamp-2">{project.title}</h3>
              <p className="text-muted-foreground text-sm">
                A <span className="font-medium">{project.product}</span> to{" "}
                <span className="font-medium">{project.action}</span> for{" "}
                <span className="font-medium">{project.customer}</span>
              </p>
            </div>


          </div>
        </CardContent>
      </Card>

      {/* Expanded Modal */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto p-0">
          {/* Hidden title for accessibility */}
          <DialogTitle className="sr-only">{project.title}</DialogTitle>
          <div className="p-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2">{project.title}</h2>
              <p className="text-muted-foreground">
                A <span className="font-semibold">{project.product}</span> to{" "}
                <span className="font-semibold">{project.action}</span> for{" "}
                <span className="font-semibold">{project.customer}</span>
              </p>
            </div>
          </div>

          {/* Media container - full width, outside of padding */}
          <div className="relative w-full bg-black flex items-center justify-center" style={{ maxHeight: 'calc(95vh - 200px)' }}>
            {isVideo ? (
              <VideoPlayer
                src={project.image_url}
                className="w-auto h-auto max-w-full max-h-full"
                autoPlay
                loop
                muted
                playsInline
                controls
              />
            ) : (
              <Image
                src={project.image_url || "/placeholder.svg"}
                alt={project.title}
                width={1920}
                height={1080}
                className="w-auto h-auto max-w-full max-h-full object-contain"
              />
            )}
          </div>

          <div className="p-6 space-y-6">


          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
